<script setup lang="ts">
/*
 * FILE: unified_ui/src/components/OnboardingModal.vue
 * PURPOSE: Full-screen one-time onboarding overlay («Кибер Бот») shown above
 *          the shell after auth. Offers two paths — КП wizard (/new-order)
 *          and agent chat (/chat) — plus a skip link that closes without
 *          navigation. Any interaction marks onboarding as seen and reports
 *          a button-click event.
 * OWNS: onboarding overlay UI + its interaction tracking.
 * EXPORTS: OnboardingModal (no props/emits — visibility is driven by the
 *          onboarding store, which App.vue reads)
 * DOCS: docs/architecture/components/unified-ui.md
 */
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useOnboardingStore } from '@/stores/onboarding'
import { trackEvent } from '@/lib/trackEvent'

const router = useRouter()
const onboarding = useOnboardingStore()

// The overlay mounts exactly when it becomes visible — "seen the welcome
// screen" is a beat of the user's path (fires once per session while the
// one-time onboarding is still un-seen).
onMounted(() => {
  trackEvent('onboarding-shown')
})

function finish(label: string, go: () => void) {
  onboarding.complete()
  trackEvent('button-click', label, { element: 'onboarding', view: 'onboarding' })
  go()
}

function goNewOrder() {
  finish('К оформлению КП', () => router.push('/new-order'))
}

function goChat() {
  finish('К чату с агентом', () => router.push('/chat'))
}

function skip() {
  finish('Пропустить', () => {})
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm dark:bg-slate-950/70"
    data-testid="onboarding-overlay"
  >
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="card-heavy w-full max-w-md p-6 text-center md:p-8">
        <h1 class="text-2xl font-semibold text-ink-900 dark:text-slate-100">Кибер Бот</h1>
        <p class="mt-2 text-sm leading-relaxed text-ink-700 dark:text-slate-300">
          В нашем боте вы можете получить КП с анализом рынка и ценами, или поискать инфу по товару/подобрать нужное
        </p>

        <div class="mt-6 space-y-6">
          <div>
            <p class="text-sm leading-relaxed text-ink-700 dark:text-slate-300">
              Знаете, какой товар хотите, для получения КП?
            </p>
            <button type="button" class="btn-primary mt-3 w-full" @click="goNewOrder">
              К оформлению КП
            </button>
          </div>

          <div>
            <p class="text-sm leading-relaxed text-ink-700 dark:text-slate-300">
              Не знаете, что ищете? Наш агент поможет разобраться и поищет в наших каталогах
            </p>
            <button type="button" class="btn-ghost mt-3 w-full" @click="goChat">
              К чату с агентом
            </button>
          </div>
        </div>

        <button
          type="button"
          class="mt-6 text-sm font-medium text-ink-500 transition-colors hover:text-brand-500 dark:text-slate-400 dark:hover:text-brand-400"
          @click="skip"
        >
          Пропустить
        </button>
      </div>
    </div>
  </div>
</template>
